import React, { useEffect, useRef, useState } from 'react';
import './InterviewSitting.css';

/**
 * The examiner's portrait plus the candidate's optional self-view. No real
 * examiner portraits exist yet (plan's own "still open" note) — imageUrl is
 * optional and falls back to an initials mark, so this is production-ready
 * the moment real assets land rather than blocked on them.
 *
 * The chosen approach (plan decision) is a portrait with an audio-reactive
 * halo, not true lip-sync: getAmplitude() is polled every animation frame
 * only while isSpeaking, driving the halo's scale/opacity via a CSS
 * variable — cheap, no canvas, and the loop stops the instant speech ends
 * rather than free-running.
 */
// Real examiner names (the mockup's own TOPICS data) are consistently
// titled - "Dr Amara Osei", "Prof. Iain Marsh" - so a naive first-two-
// words split produces "Dr"+first-name initials for nearly every real
// name, not first+last. Titles are stripped first, so the fallback shows
// the initials a person would actually recognise as their own.
const NAME_TITLES = new Set(['dr', 'prof', 'prof.', 'professor', 'mr', 'mrs', 'ms', 'miss', 'mx']);

function fallbackInitials(name) {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  const significant = words.filter((w) => !NAME_TITLES.has(w.toLowerCase().replace(/\.$/, '')));
  const source = significant.length ? significant : words;
  if (!source.length) return '?';
  return source.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

export default function InterviewAvatarStage({
  examinerName,
  examinerRole,
  imageUrl,
  isSpeaking,
  getAmplitude,
  cameraEnabled,
  onToggleCamera,
  avatar = null,
  faceHeld = false,
}) {
  const haloRef = useRef(null);
  const frameRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraError, setCameraError] = useState('');

  useEffect(() => {
    if (!isSpeaking) {
      if (haloRef.current) haloRef.current.style.setProperty('--amp', '0');
      return undefined;
    }
    const tick = () => {
      const amp = getAmplitude ? getAmplitude() : 0;
      if (haloRef.current) haloRef.current.style.setProperty('--amp', String(amp));
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [isSpeaking, getAmplitude]);

  useEffect(() => {
    if (!cameraEnabled) {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      return undefined;
    }
    let cancelled = false;
    setCameraError('');
    navigator.mediaDevices?.getUserMedia({ video: true })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(() => { if (!cancelled) setCameraError('Could not access the camera.'); });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    };
  }, [cameraEnabled]);

  const initials = fallbackInitials(examinerName);
  // A lip-synced examiner on trial (avatar/useAvatar.js) fills the stage like
  // a video call; the initials stay until it is ready and come back if it
  // fails.
  const avatarOn = Boolean(avatar && avatar.status !== 'off');
  // faceHeld: a line is playing without the face (it wasn't ready in time),
  // so the face waits for the next line rather than appear mid-sentence.
  const avatarReady = avatarOn && avatar.status === 'ready' && !faceHeld;

  // The face's elements belong to useAvatar (its session can open before
  // this stage exists); the stage only shows them.
  const hostRef = useRef(null);
  const dock = avatar?.dock;
  const undock = avatar?.undock;
  useEffect(() => {
    if (!avatarOn || !dock || !hostRef.current) return undefined;
    dock(hostRef.current);
    return () => undock?.();
  }, [avatarOn, dock, undock]);
  const avatarVideo = avatar?.video;
  useEffect(() => {
    if (avatarVideo) avatarVideo.hidden = !avatarReady;
  }, [avatarVideo, avatarReady]);

  return (
    <div className="is-stage">
      {avatarOn && <div ref={hostRef} className="is-avatar-host" />}
      <div className="is-portrait-wrap" hidden={avatarReady}>
        <div className="is-halo" ref={haloRef} aria-hidden="true" />
        {imageUrl ? (
          <img className="is-portrait" src={imageUrl} alt={examinerName || 'Interviewer'} />
        ) : (
          <div className="is-portrait is-portrait--fallback">{initials}</div>
        )}
      </div>

      <div className="is-nameplate">
        <span className={`is-live-dot${isSpeaking ? ' is-live-dot--active' : ''}`} />
        <span className="is-nameplate-name">{examinerName || 'Interviewer'}</span>
        {examinerRole && <span className="is-nameplate-role">{examinerRole}</span>}
      </div>

      {/* With the camera off there is nothing to show; over a talking face
          the placeholder only covers it. */}
      {(cameraEnabled || !avatarReady) && (
        <div className="is-selfview">
          {cameraEnabled ? (
            <video ref={videoRef} autoPlay playsInline muted className="is-selfview-video" />
          ) : (
            <div className="is-selfview-off">Camera off</div>
          )}
        </div>
      )}
      <button type="button" className="is-ctrl is-camera-toggle" onClick={onToggleCamera}>
        {cameraEnabled ? 'Turn camera off' : 'Turn camera on'}
      </button>
      {cameraError && <div className="is-camera-error">{cameraError}</div>}
    </div>
  );
}
